import { Colors, Lightning, Router } from '@lightningjs/sdk';
import { translate } from 'support/translate';
import { getImageTextureObj } from 'support/generalUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import Button from 'components/common/buttons/Button';
import { RoutePath } from 'config/routes';
import TextLabel from 'components/common/TextLabel';

export interface LoggedOutProfileTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Logo: typeof Lightning.Texture;
  Text: {
    Header: typeof TextLabel;
    Description: typeof TextLabel;
  };
  GetStartedButton: typeof Button;
}

const SECTION_WIDTH = 640;

const LOGGED_OUT_PROFILE_CONTAINER_WIDTH = 432;
const LOGGED_OUT_PROFILE_CONTAINER_HEIGHT = 358;
const LOGGED_OUT_PROFILE_LOGO_HEIGHT = 60;
const LOGGED_OUT_PROFILE_HEADER_FONT_SIZE = 56;
const LOGGED_OUT_PROFILE_DESCRIPTION_FONT_SIZE = 24;
const LOGGED_OUT_PROFILE_BUTTON_HEIGHT = 65;
const LOGGED_OUT_PROFILE_BUTTON_FONT_SIZE = 18;

const VERTICAL_SPACE_LARGE = 24;
const VERTICAL_SPACE_MEDIUM = 10;
const VERTICAL_SPACE_SMALL = 8;

export default class LoggedOutProfile
  extends Lightning.Component<LoggedOutProfileTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<LoggedOutProfileTemplateSpec>
{
  static override _template(): Lightning.Component.Template<LoggedOutProfileTemplateSpec> {
    const cwLogo = getImageTextureObj('static/images/brand-logo.svg', 136, 60);

    return {
      h: LOGGED_OUT_PROFILE_CONTAINER_HEIGHT,
      w: SECTION_WIDTH,
      flex: {
        direction: 'column',
        alignItems: 'center',
      },
      flexItem: { marginTop: VERTICAL_SPACE_SMALL },
      Logo: {
        ...cwLogo,
        h: LOGGED_OUT_PROFILE_LOGO_HEIGHT,
        flexItem: {
          marginTop: VERTICAL_SPACE_SMALL,
          marginBottom: VERTICAL_SPACE_MEDIUM,
        },
      },
      Text: {
        w: LOGGED_OUT_PROFILE_CONTAINER_WIDTH,
        flex: {
          direction: 'column',
        },
        flexItem: { marginBottom: VERTICAL_SPACE_LARGE },
        Header: {
          type: TextLabel,
          text: {
            text: translate('settings.LoggedOutProfile.Header'),
            fontSize: LOGGED_OUT_PROFILE_HEADER_FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
          },
          flexItem: {
            marginTop: VERTICAL_SPACE_MEDIUM,
            marginBottom: 4,
          },
        },
        Description: {
          type: TextLabel,
          text: {
            text: translate('settings.LoggedOutProfile.Description'),
            fontSize: LOGGED_OUT_PROFILE_DESCRIPTION_FONT_SIZE,
            fontFace: getFontFaceFromStyle('light'),
          },
          flexItem: { marginBottom: VERTICAL_SPACE_SMALL },
        },
      },
      GetStartedButton: {
        type: Button,
        flexItem: {
          marginTop: VERTICAL_SPACE_LARGE,
          marginBottom: VERTICAL_SPACE_LARGE,
        },
        height: LOGGED_OUT_PROFILE_BUTTON_HEIGHT,
        minWidth: SECTION_WIDTH,
        label: translate('settings.LoggedOutProfile.button'),
        title: [
          translate('settings.LoggedOutProfile.Header'),
          translate('settings.LoggedOutProfile.Description'),
          translate('settings.LoggedOutProfile.button'),
        ],
        fontSize: LOGGED_OUT_PROFILE_BUTTON_FONT_SIZE,
        fontFace: getFontFaceFromStyle('bold'),
        radius: 5,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          stroke: 2,
          strokeColor: Colors('white').get(),
          radius: 5,
        },
        backgroundColor: {
          focused: Colors('white').get(),
          unfocused: Colors('alternateBackground').get(),
        },
        fontColor: {
          focused: Colors('black').get(),
          unfocused: Colors('text').get(),
        },
        passSignals: {
          $onHover: '$onHover',
        },
        action: '$getStarted',
        signals: {
          $getStarted: '$getStarted',
        },
      },
    };
  }

  private $getStarted() {
    Router.navigate(RoutePath.LOGIN);
  }
}
